import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

// import { Checkbox } from "antd";
import { toast } from "react-toastify";

import useAbortController from "../../../Common/hooks/useAbortController";
import StyledSegmented from "../../../Common/UIComponents/StyledSegmented";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import ConfirmationModal from "../../../Onboarding/UpdateOnboardingModal/ConfirmationModal";
import ActionCell from "../../Common/ActionCell";
import RequestTable from "../../Common/RequestTable";
import SearchAndCreate from "../../Common/SearchAndCreate";
import SpecialInstructionsModal from "../../Common/SpecialInstructionsModal";
import SupplyPickupLanding from "../../SupplyPickupLanding";
import ViewRecurringRequestModal from "./ViewRecurringRequestModal";
import ViewSupplyRequestModal from "./ViewSupplyRequestModal";

import {
  fetchSupplyRequests, fetchRecurringRequests, editSupplyRequest, editSupplyRecurrenceRequest,
} from "../../../../actions/supplyPickup";

import { ReactComponent as ExclamationIcon } from "../../../../assets/images/sample-submission/exclamationIcon.svg";
import { ReactComponent as ShoppingCart } from "../../../../assets/images/supply-pickup/ShoppingCart.svg";

import "../SupplyPickupTabGroup.css";

const supplyRequestTypeOptions = [
  { value: "normal", label: "Normal Requests" },
  { value: "recurring", label: "Recurring Requests" },
];

const getRequestTypeApiParams = (requestType) => {
  if (requestType === "normal") return { request_type: "normal" };
  if (requestType === "recurring") return { request_type: "recurring" };
  return {};
};

const defaultRecurringPaginationInfo = { nextPageLink: "", currentPage: 1, totalPages: 1 };

const getRecurringPaginationInfo = ({ count = 0, result = [], next = "" }, currentPage) => {
  const hasNext = Boolean(next);
  return {
    nextPageLink: next || "",
    currentPage,
    totalPages: hasNext ? currentPage + 1 : currentPage,
    count,
    hasMore: hasNext,
  };
};

const getRecurringTotalCount = ({ count = 0, result = [] }) => count || result.length;

const buildRecurringCancelPayload = (request) => ({
  items: (request.items ?? []).map(({ inventory_item_id, quantity, status }) => ({
    inventory_item_id,
    quantity,
    status: status ?? "requested",
  })),
  delivery_details: request.delivery_details ?? {
    name: request.contact_name,
    email: request.email,
    phone: request.phone,
    address: request.address,
  },
  comments: request.comments ?? "",
  frequency: request.frequency,
  requested_by: request.requested_by,
  supply_request_id: request.supply_request_id,
  status: "cancelled",
});

export default function SupplyTab({
  onCreateEditClick,
  initialSupplyResponse = null,
  initialRecurringResponse = null,
  showLanding = false,
  onSupplyDataMutated,
}) {
  const { areResultsLocked } = useAccessStatus();
  const columns = [
    { name: "Order ID", key: "supply_order_id" },
    {
      name: "Tracking Number",
      key: "tracking_id",
      header: (
        <div className="SupplyTableTrackingHeader">
          <StyledTooltip
            title="Tracking order is available when the order is on the way"
            placement="top"
            className="SupplyTableTrackingHeader__Tooltip"
          >
            <ExclamationIcon className="SupplyTableExclamationIcon" />
          </StyledTooltip>
          <span>Tracking Number</span>
        </div>
      ),
    },
    {
      name: "Expected Ship Date",
      key: "expected_ship_date",
    },
    {
      name: "Items",
      key: "items",
    },
    { name: "Order Date", key: "date" },
    { name: "Requested By", key: "requested_by" },
    { name: "Request Type", key: "request_type" },
    { name: "Status", key: "status" },
    {
      name: " ",
      key: "action",
      className: "SupplyTableAction",
      cell: <ActionCell />,
    },
  ];
  const [loading, setLoading] = useState(true);
  const [loadingNewContent, setLoadingNewContent] = useState(false);
  const [requests, setRequests] = useState([]);
  const [reportDates, setReportDates] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({ nextPageLink: "", currentPage: 1, totalPages: 1 });
  const requestTableScrollbarRef = useRef();
  const [search, setSearch] = useState("");
  const [dates, setDates] = useState({ date_from: "", date_to: "" });
  const [requestTypeFilter, setRequestTypeFilter] = useState("");
  const [recurringRequestsCount, setRecurringRequestsCount] = useState(0);
  const [recurringRequestsLoading, setRecurringRequestsLoading] = useState(false);
  const statuses = [
    { value: "requested", label: "Requested" },
    { value: "in_progress", label: "In Progress" },
    { value: "in_transit", label: "In Transit" },
    // { value: "not_approved", label: "Not Approved" },
    { value: "delivered", label: "Delivered" },
  ];
  const defaultStatus = "exclude-cancelled";
  const [currentStatus, setCurrentStatus] = useState(defaultStatus);
  // Cancelled
  const extraStatuses = [
    { value: "cancelled", label: "Cancelled" },
  ];
  const [extraStatus, setExtraStatus] = useState(defaultStatus);
  const [actionMap, setActionMap] = useState({
    menu: {
      cancel: true,
      edit: true,
    },
    comment: true,
  });
  const resolvedActionMap = useMemo(() => {
    if (!actionMap?.menu) {
      return actionMap;
    }
    return {
      ...actionMap,
      menu: {
        ...actionMap.menu,
        edit: Boolean(actionMap.menu.edit) && !areResultsLocked,
      },
    };
  }, [actionMap, areResultsLocked]);
  const [spInstructionData, setSpInstructionData] = useState({
    open: false,
    data: {},
  });
  const [deleteRequestData, setDeleteRequestData] = useState({
    open: false,
    data: {},
  });
  const [viewRequestData, setViewRequestData] = useState({
    open: false,
    loading: false,
    data: {},
  });
  const [viewRecurringRequestData, setViewRecurringRequestData] = useState({
    open: false,
    data: [],
    paginationInfo: defaultRecurringPaginationInfo,
  });
  const [recurringModalLoadingNewContent, setRecurringModalLoadingNewContent] = useState(false);
  const recurringModalFetchingRef = useRef(false);
  const [cancelRecurringRequestData, setCancelRecurringRequestData] = useState({
    open: false,
    loading: false,
    data: {},
  });

  const { setNewAbortController, clearAbortController } = useAbortController();

  const fetchRequests = async (params = {}, callback = () => { }) => {
    const page = params.page !== undefined ? Number(params.page) : 1;

    if (page > 1) {
      setLoadingNewContent(true);
    } else {
      setLoading(true);
      if (requestTableScrollbarRef.current) {
        requestTableScrollbarRef.current.scrollToTop();
      }
    }

    const newAbortController = setNewAbortController();
    const requestType = params.requestType !== undefined ? params.requestType : requestTypeFilter;
    const payload = {
      supply_order_id: params.search !== undefined ? params.search : search,
      status: params.status !== undefined ? params.status : (currentStatus || extraStatus || defaultStatus),
      date_from: params.date_from !== undefined ? params.date_from : dates.date_from,
      date_to: params.date_to !== undefined ? params.date_to : dates.date_to,
      page,
      ...getRequestTypeApiParams(requestType),
    };
    const {
      success, message, result, canceled, dates: rpDates, next_page_link, total_page,
    } = await fetchSupplyRequests(payload, newAbortController.signal);

    if (newAbortController.signal.aborted) {
      if (page > 1) {
        setLoadingNewContent(false);
      }
      return;
    }

    if (success) {
      setPaginationInfo({ nextPageLink: next_page_link, currentPage: page, totalPages: total_page });
      setRequests((prev) => (page > 1 ? [...prev, ...result] : result));
      setReportDates(rpDates);
    } else if (!canceled) {
      toast.error(message);
      if (page > 1) {
        if (requestTableScrollbarRef.current) {
          requestTableScrollbarRef.current.scrollTop -= 100;
        }
      } else {
        setPaginationInfo({ nextPageLink: "", currentPage: 1, totalPages: 1 });
        setRequests([]);
        setReportDates([]);
      }
    }

    clearAbortController();

    if (page > 1) {
      if (requestTableScrollbarRef.current) {
        requestTableScrollbarRef.current.scrollTop -= 4;
      }
      setLoadingNewContent(false);
    } else {
      setLoading(false);
      if (requestTableScrollbarRef.current) {
        requestTableScrollbarRef.current.scrollToTop();
      }
    }

    if (success || !canceled) {
      callback();
    }
  };

  const loadRecurringRequests = async (page = 1) => {
    const response = await fetchRecurringRequests({ page });
    const { success } = response;
    if (success) {
      setRecurringRequestsCount(getRecurringTotalCount(response));
    }
    return response;
  };

  const fetchRecurringRequestsForModal = async (page = 1) => {
    if (page > 1) {
      if (recurringModalFetchingRef.current) return null;
      recurringModalFetchingRef.current = true;
      setRecurringModalLoadingNewContent(true);
    }

    const response = await fetchRecurringRequests({ page });
    const {
      success, message, canceled, result = [], next = "",
    } = response;

    if (success) {
      setViewRecurringRequestData((prev) => {
        const isEmptyFollowUpPage = page > 1 && result.length === 0;
        return {
          ...prev,
          data: page > 1 && !isEmptyFollowUpPage ? [...prev.data, ...result] : (page > 1 ? prev.data : result),
          paginationInfo: isEmptyFollowUpPage
            ? {
              ...prev.paginationInfo, nextPageLink: "", hasMore: false, totalPages: prev.paginationInfo.currentPage,
            }
            : getRecurringPaginationInfo({ count: response.count, result, next }, page),
        };
      });
      if (page === 1) {
        setRecurringRequestsCount(getRecurringTotalCount(response));
      }
    } else if (!canceled) {
      toast.error(message);
    }

    if (page > 1) {
      recurringModalFetchingRef.current = false;
      setRecurringModalLoadingNewContent(false);
    }

    return response;
  };

  const applySupplyResponse = (response) => {
    const {
      success, message, result, canceled, dates: rpDates, next_page_link, total_page,
    } = response;
    if (success) {
      setPaginationInfo({ nextPageLink: next_page_link, currentPage: 1, totalPages: total_page });
      setRequests(result || []);
      setReportDates(rpDates);
    } else if (!canceled) {
      toast.error(message);
      setPaginationInfo({ nextPageLink: "", currentPage: 1, totalPages: 1 });
      setRequests([]);
      setReportDates([]);
    }
    setLoading(false);
  };

  const applyRecurringResponse = (response) => {
    const { success } = response;
    if (success) {
      setRecurringRequestsCount(getRecurringTotalCount(response));
    }
  };

  useEffect(() => {
    if (initialSupplyResponse && initialRecurringResponse) {
      applySupplyResponse(initialSupplyResponse);
      applyRecurringResponse(initialRecurringResponse);
    }
  }, [initialSupplyResponse, initialRecurringResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loadingNewContent && requestTableScrollbarRef.current) {
      requestTableScrollbarRef.current.scrollToBottom();
    }
  }, [loadingNewContent]);

  const handleStatusOnChange = (value, updateStatusCallback) => {
    const status = value || defaultStatus;
    updateStatusCallback(value);
    if (status === "delivered") {
      setActionMap({
        comment: true,
      });
    } else {
      setActionMap({
        menu: {
          cancel: true,
          edit: true,
        },
        comment: true,
      });
    }
    fetchRequests({ status, page: 1 });
  };

  const handleDeleteRequest = async () => {
    setDeleteRequestData((prev) => {
      const newData = { ...prev };
      newData.loading = true;
      return newData;
    });
    const { supply_order_id, requested_by, request_type } = deleteRequestData.data;
    const payload = {
      status: "cancelled",
      supply_order_id,
      requested_by,
      request_type: request_type?.toLowerCase() === "recurring" ? "recurring" : "normal",
    };
    const { success, message } = await editSupplyRequest(payload);
    if (success) {
      setDeleteRequestData({ open: false, loading: false, data: {} });
      toast.success(message);
      fetchRequests({ page: 1 });
      loadRecurringRequests();
      if (onSupplyDataMutated) {
        onSupplyDataMutated();
      }
    } else {
      toast.error(message);
      setDeleteRequestData((prev) => {
        const newData = { ...prev };
        newData.loading = false;
        return newData;
      });
    }
  };

  const handleSearch = (params) => {
    fetchRequests({ ...params, page: 1 });
  };

  const handleCloseComment = (refresh = false) => {
    setSpInstructionData({ open: false, data: {} });
    if (refresh) {
      fetchRequests({ page: 1 });
    }
  };

  const handleCloseViewModal = (refresh = false) => {
    setViewRequestData({ open: false, data: {} });
    if (refresh) {
      fetchRequests({ page: 1 });
    }
  };

  const handleSupplyRequestTypeFilterChange = (value) => {
    const requestType = value || "";
    setRequestTypeFilter(requestType);
    fetchRequests({ requestType, page: 1 });
  };

  const openRecurringRequestFlow = (response = {}) => {
    const { result = [] } = response;
    if (getRecurringTotalCount(response) === 0) {
      onCreateEditClick({
        pageTitle: "Create Recurring Supply Request",
        isRecurring: true,
      });
      return;
    }
    setViewRecurringRequestData({
      open: true,
      data: result,
      paginationInfo: getRecurringPaginationInfo(response, 1),
    });
  };

  const handleRecurringRequestsClick = async () => {
    setRecurringRequestsLoading(true);
    try {
      const response = await fetchRecurringRequestsForModal(1);
      const { success, message, canceled } = response;
      if (!success && !canceled) {
        toast.error(message);
        return;
      }
      openRecurringRequestFlow(response);
    } finally {
      setRecurringRequestsLoading(false);
    }
  };

  const handleCloseRecurringViewModal = () => {
    recurringModalFetchingRef.current = false;
    setRecurringModalLoadingNewContent(false);
    setViewRecurringRequestData({
      open: false,
      data: [],
      paginationInfo: defaultRecurringPaginationInfo,
    });
  };

  const handleEditRecurringRequest = async (request) => {
    const {
      success, message, canceled, result = [],
    } = await fetchRecurringRequests({ supply_request_id: request.supply_request_id });
    if (!success && !canceled) {
      toast.error(message);
      return;
    }
    const editData = result[0];
    if (!editData) {
      toast.error("Recurring request not found.");
      return;
    }
    handleCloseRecurringViewModal();
    onCreateEditClick({
      pageTitle: "Edit Recurring Supply Request",
      isRecurring: true,
      data: {
        ...editData,
        supply_request_id: editData.supply_request_id || request.supply_request_id,
      },
    });
  };

  const handleCancelRecurringRequestClick = (request) => {
    setCancelRecurringRequestData({ open: true, loading: false, data: request });
  };

  const handleConfirmCancelRecurringRequest = async () => {
    setCancelRecurringRequestData((prev) => ({ ...prev, loading: true }));
    const { data: request } = cancelRecurringRequestData;

    const {
      success: fetchSuccess, message: fetchMessage, canceled, result = [],
    } = await fetchRecurringRequests({ supply_request_id: request.supply_request_id });

    if (!fetchSuccess && !canceled) {
      toast.error(fetchMessage);
      setCancelRecurringRequestData((prev) => ({ ...prev, loading: false }));
      return;
    }

    const fullRequest = result[0] ?? request;
    const payload = buildRecurringCancelPayload(fullRequest);
    const { success, message } = await editSupplyRecurrenceRequest(payload);

    if (success) {
      setCancelRecurringRequestData({ open: false, loading: false, data: {} });
      toast.success(message);

      const response = await fetchRecurringRequestsForModal(1);
      if (response.success) {
        if (getRecurringTotalCount(response) === 0) {
          handleCloseRecurringViewModal();
        }
      }

      fetchRequests({ page: 1 });
      if (onSupplyDataMutated) {
        onSupplyDataMutated();
      }
    } else {
      toast.error(message);
      setCancelRecurringRequestData((prev) => ({ ...prev, loading: false }));
    }
  };

  if (showLanding) {
    return (
      <SupplyPickupLanding
        tab="supply"
        onCreateSupplyClick={() => onCreateEditClick({ pageTitle: "Create Supply Request" })}
      />
    );
  }

  return (
    <div className="SupplyPickupMainDiv SupplyPickupMainDiv--supply">
      <div className="SupplyPickupStickyToolbar">
        <SearchAndCreate
          createBtnLabel="Create Supply Request"
          btnIcon={<ShoppingCart className="SupplyPickupTab__CreateBtnIcon" />}
          btnClassName="SupplyPickupCreateBtn"
          btnOnClick={() => onCreateEditClick({ pageTitle: "Create Supply Request" })}
          createDisabled={areResultsLocked}
          secondaryBtnLabel={`Recurring Requests (${recurringRequestsCount})`}
          secondaryBtnClassName="SupplyPickupRecurringBtn"
          secondaryBtnLoading={recurringRequestsLoading}
          secondaryBtnOnClick={handleRecurringRequestsClick}
          handleSearch={handleSearch}
          search={search}
          setSearch={setSearch}
          dates={dates}
          setDates={setDates}
          getReportDates={() => reportDates}
          supplyRequestTypeOptions={supplyRequestTypeOptions}
          supplyRequestTypeFilter={requestTypeFilter}
          handleSupplyRequestTypeFilterChange={handleSupplyRequestTypeFilterChange}
        />
        <div className="SupplyPickupFilterBar">
          <StyledSegmented
            className="SupplyTableSegmented"
            size="large"
            options={statuses}
            onChange={(v) => handleStatusOnChange(v, (statusVal) => { setCurrentStatus(statusVal); setExtraStatus(statusVal ? "" : defaultStatus); })}
            value={currentStatus}
          />
          <div className="SupplyPickupFilterSeparator" />
          <StyledSegmented
            className="SupplyTableSegmented"
            size="large"
            options={extraStatuses}
            onChange={(v) => handleStatusOnChange(v, (statusVal) => { setExtraStatus(statusVal || defaultStatus); setCurrentStatus(""); })}
            value={extraStatus}
          />
        </div>
      </div>
      <RequestTable
        loading={loading}
        loadingNewContent={loadingNewContent}
        tableData={requests}
        columns={columns}
        onCreateEditClick={onCreateEditClick}
        onDeleteClick={setDeleteRequestData}
        onRowClick={setViewRequestData}
        onSpInstructionClick={setSpInstructionData}
        actionMap={resolvedActionMap}
        paginationInfo={paginationInfo}
        fetchRequests={fetchRequests}
        scrollbarRef={requestTableScrollbarRef}
        search={search}
      />
      <ViewSupplyRequestModal
        open={viewRequestData.open}
        data={viewRequestData.data}
        onClose={handleCloseViewModal}
      />
      <ViewRecurringRequestModal
        open={viewRecurringRequestData.open}
        data={viewRecurringRequestData.data}
        paginationInfo={viewRecurringRequestData.paginationInfo}
        fetchRecurringRequests={fetchRecurringRequestsForModal}
        loadingNewContent={recurringModalLoadingNewContent}
        onClose={handleCloseRecurringViewModal}
        onEditClick={handleEditRecurringRequest}
        onCancelClick={handleCancelRecurringRequestClick}
      />
      <SpecialInstructionsModal
        open={spInstructionData.open}
        data={spInstructionData.data}
        onClose={handleCloseComment}
      />
      {deleteRequestData.open && (
        <ConfirmationModal
          headerText="Cancel Supply Request Confirmation"
          bodyText="Are you sure you want to cancel this request?"
          buttonText={["Close", "Confirm"]}
          buttonFunctions={[() => setDeleteRequestData({ open: false, loading: false, data: {} }), handleDeleteRequest]}
          loading={deleteRequestData.loading}
        />
      )}
      {cancelRecurringRequestData.open && (
        <ConfirmationModal
          headerText="Cancel Recurring Request Confirmation"
          bodyText="Are you sure you want to cancel this recurring request?"
          buttonText={["Close", "Confirm"]}
          buttonFunctions={[
            () => setCancelRecurringRequestData({ open: false, loading: false, data: {} }),
            handleConfirmCancelRecurringRequest,
          ]}
          loading={cancelRecurringRequestData.loading}
        />
      )}
    </div>
  );
}
